import { useEffect, useState } from 'react';

/**
 * End-of-day note popup. Appears when the employee clicks Check Out.
 * Skip and Save are equally available. Dismiss via X / Escape / click outside
 * also checks out without saving a note.
 */
export function EndOfDayNoteModal({ open, totalHoursLabel, sessionsCount, onCheckOut, onDismiss }) {
    const [note, setNote] = useState('');

    useEffect(() => { if (open) setNote(''); }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onDismiss(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onDismiss]);

    if (!open) return null;

    const handleBackdrop = (e) => { if (e.target === e.currentTarget) onDismiss(); };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                zIndex: 50,
            }}
            onClick={handleBackdrop}
        >
            <div
                role="dialog"
                aria-modal="true"
                className="surface-card"
                style={{ padding: 22, width: '100%', maxWidth: 440, position: 'relative' }}
            >
                <button
                    aria-label="Close"
                    onClick={onDismiss}
                    style={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--color-text-tertiary)',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 'var(--border-radius-md)',
                        cursor: 'pointer',
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 14 14">
                        <path d="M3 3 L11 11 M11 3 L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </button>

                <h2 style={{ fontSize: 15, fontWeight: 500, marginBottom: 6, paddingRight: 24 }}>
                    Wrap up your day
                </h2>
                <p
                    style={{
                        fontSize: 13,
                        color: 'var(--color-text-secondary)',
                        marginBottom: 14,
                        lineHeight: 1.55,
                    }}
                >
                    You worked{' '}
                    <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>
                        {totalHoursLabel}
                    </span>
                    {sessionsCount > 1 ? ` across ${sessionsCount} sessions` : ''} today. Add an optional note before checking out.
                </p>

                <textarea
                    autoFocus
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional — leave blank to skip"
                    className="textarea"
                    style={{ minHeight: 96, marginBottom: 14 }}
                />

                <div className="flex justify-between" style={{ gap: 8 }}>
                    <button
                        type="button"
                        onClick={() => onCheckOut(null)}
                        className="btn btn-ghost"
                    >
                        Skip and check out
                    </button>
                    <button
                        type="button"
                        onClick={() => onCheckOut(note.trim() || null)}
                        className="btn btn-primary"
                    >
                        Save and check out
                    </button>
                </div>
            </div>
        </div>
    );
}
